import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Gavel, Clock, TrendingDown, Users, Send, Crown, Check, X,
  Loader2, AlertCircle, Plus, Trophy, ChevronDown, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";

const eur = (v) => v == null ? "—" : new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v));

function timeLeft(deadline) {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms <= 0) return "abgelaufen";
  const h = Math.floor(ms / 3.6e6);
  const m = Math.floor((ms % 3.6e6) / 6e4);
  if (h < 1) return `${m} min`;
  if (h < 24) return `${h} h ${m} min`;
  return `${Math.floor(h / 24)} Tag${Math.floor(h / 24) === 1 ? "" : "e"}`;
}

/**
 * Auktion-Panel für das Back-Office-Cockpit.
 * Zeigt den Status der Auktion zum Projekt und ermöglicht Start, Live-Monitor
 * und Abwicklung der Runner-Up-Bestätigungs-Tasks.
 */
export default function ProjectAuctionPanel({ projectId }) {
  const [auction, setAuction] = useState(null);
  const [pendingTask, setPendingTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/auction`);
      setAuction(data);
      if (data?.status === "awaiting_decision" && data?.winner_response === "rejected") {
        // Find runner-up task
        const t = await api.get("/admin/auction-runnerup-tasks");
        const match = (t.data || []).find((x) => (x.meta || {}).auction_id === data.id);
        setPendingTask(match || null);
      } else {
        setPendingTask(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auktion konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (loading) {
    return <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-3 text-zinc-400 text-[12px] inline-flex items-center gap-2 font-medium"><Loader2 size={13} className="animate-spin" /> Auktion lädt…</div>;
  }

  // ───── No auction yet → start button ─────
  if (!auction) {
    return (
      <>
        <button
          onClick={() => setShowStart(true)}
          data-testid="auction-start-btn"
          className="w-full bg-[#18181B] border border-dashed border-zinc-700 hover:border-[#00E575]/50 hover:bg-[#00E575]/5 rounded-xl p-3 inline-flex items-center justify-center gap-2 text-[12.5px] text-zinc-300 transition"
        >
          <Gavel size={14} className="text-[#00E575]" /> Montage-Auktion starten
        </button>
        {showStart && <StartAuctionModal projectId={projectId} onClose={() => setShowStart(false)} onCreated={load} />}
      </>
    );
  }

  // ───── Has auction → status panel ─────
  const status = auction.status;
  const tl = timeLeft(auction.deadline);
  const isOpen = status === "open";
  const isAwarded = status === "awarded";
  const isAwaitingDecision = status === "awaiting_decision";
  const isNoBids = status === "closed_no_bids" || status === "closed_no_winner";

  const handleAward = async () => {
    if (!pendingTask) return;
    if (!window.confirm(`Auftrag an Kandidat (${eur(pendingTask.meta.candidate_bid_eur)}) vergeben?\n\nNeue Frist: 24 Stunden zur Bestätigung.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/auction-tasks/${pendingTask.id}/award`);
      toast.success("Auftrag an Kandidat vergeben — 24h-Frist läuft.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vergabe fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    if (!pendingTask) return;
    if (!window.confirm("Auktion endgültig schließen?\n\nKein weiterer Kandidat — Montage muss manuell zugewiesen werden.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/auction-tasks/${pendingTask.id}/dismiss`);
      toast.info("Auktion ohne Gewinner geschlossen.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Schließung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-xl overflow-hidden" data-testid="bo-auction-panel">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between p-3 hover:bg-zinc-900/50"
      >
        <div className="flex items-center gap-2">
          <Gavel className="text-[#00E575]" size={15} />
          <span className="text-[12.5px] font-bold text-zinc-100">Montage-Auktion</span>
          <StatusBadge status={status} responseTimeout={isAwaitingDecision && auction.winner_response} />
          {tl && isOpen && <span className="text-[12px] text-amber-300 inline-flex items-center gap-0.5"><Clock size={10} /> {tl}</span>}
        </div>
        {expanded ? <ChevronDown size={14} className="text-zinc-500" /> : <ChevronRight size={14} className="text-zinc-500" />}
      </button>

      {expanded && (
        <div className="p-3 pt-0 space-y-2">
          <div className="grid grid-cols-3 gap-2 text-[12px]">
            <Stat icon={TrendingDown} label="Bestes Gebot" value={auction.current_best_bid_eur != null ? eur(auction.current_best_bid_eur) : "—"} />
            <Stat icon={Users} label="Bieter" value={String(auction.current_bid_count || 0)} />
            <Stat icon={Crown} label="Reserve (max)" value={auction.reserve_price_eur != null ? eur(auction.reserve_price_eur) : "—"} />
          </div>

          {/* Awaiting decision banner — winner responded or pending */}
          {isAwarded && (
            <div className="bg-[#00E575]/10 border border-[#00E575]/30 rounded-lg p-2.5 text-[11.5px] text-[#00E575] inline-flex items-center gap-1.5">
              <Trophy size={13} /> Auftrag vergeben — Gewinner hat bestätigt.
            </div>
          )}

          {isAwaitingDecision && !pendingTask && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 text-[11.5px] text-amber-200 flex items-start gap-1.5">
              <Clock size={13} className="shrink-0 mt-0.5" />
              <span>Gewinner ({eur(auction.winning_bid_eur)}) hat 24 h Bedenkzeit. Frist: {auction.winner_response_deadline && new Date(auction.winner_response_deadline).toLocaleString("de-DE")}</span>
            </div>
          )}

          {/* Pending runner-up task with action buttons */}
          {pendingTask && (
            <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 space-y-2" data-testid="bo-runnerup-task">
              <div className="flex items-start gap-2">
                <AlertCircle size={14} className="text-red-300 shrink-0 mt-0.5" />
                <div className="flex-1 text-[11.5px]">
                  <div className="font-bold text-red-200 mb-0.5">Vorheriger Gewinner hat abgelehnt</div>
                  <div className="text-zinc-300">
                    Nächst-niedrigstes Gebot: <b className="text-zinc-100">{eur(pendingTask.meta.candidate_bid_eur)}</b>
                  </div>
                  <div className="text-zinc-500 text-[10.5px] mt-0.5">Aufgabe fällig: {pendingTask.due_date && new Date(pendingTask.due_date).toLocaleString("de-DE")}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleAward} disabled={busy} data-testid="bo-runnerup-award" className="flex-1 h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[11.5px] inline-flex items-center justify-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Vergeben (24 h Frist)
                </button>
                <button onClick={handleDismiss} disabled={busy} data-testid="bo-runnerup-dismiss" className="h-9 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 text-[11.5px] inline-flex items-center gap-1 hover:border-red-500/40 hover:text-red-300 disabled:opacity-50">
                  <X size={12} /> Schließen
                </button>
              </div>
            </div>
          )}

          {isNoBids && (
            <div className="bg-zinc-800/40 border border-zinc-700 rounded-lg p-2.5 text-[11.5px] text-zinc-300 inline-flex items-center gap-1.5">
              <X size={13} /> Keine Gebote / kein passender Installateur. Bitte manuell zuweisen.
            </div>
          )}

          <div className="text-[9.5px] text-zinc-500">
            Auktion seit {auction.starts_at && new Date(auction.starts_at).toLocaleDateString("de-DE")} ·
            Deadline {auction.deadline && new Date(auction.deadline).toLocaleString("de-DE")}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#09090B] border border-zinc-800 rounded-lg p-2">
      <div className="text-[12px] uppercase tracking-widest text-zinc-200 font-semibold font-bold inline-flex items-center gap-1"><Icon size={9} />{label}</div>
      <div className="text-[12px] font-bold text-zinc-100 mt-0.5">{value}</div>
    </div>
  );
}

function StatusBadge({ status, responseTimeout }) {
  const map = {
    open: { lbl: "Aktiv", cls: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/30" },
    awaiting_decision: { lbl: "Wartet auf Annahme", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
    awarded: { lbl: "Vergeben", cls: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/30" },
    closed_no_bids: { lbl: "Keine Gebote", cls: "bg-zinc-700/30 text-zinc-300 border-zinc-700" },
    closed_no_winner: { lbl: "Geschlossen", cls: "bg-zinc-700/30 text-zinc-300 border-zinc-700" },
  };
  const meta = map[status] || { lbl: status, cls: "bg-zinc-700/30 text-zinc-300 border-zinc-700" };
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.cls}`}>{meta.lbl}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Start-Auction Modal
// ─────────────────────────────────────────────────────────────────────────────
function StartAuctionModal({ projectId, onClose, onCreated }) {
  const defaultDeadline = new Date(Date.now() + 7 * 24 * 3.6e6).toISOString().slice(0, 16);
  const [form, setForm] = useState({
    deadline: defaultDeadline,
    reserve_price_eur: "",
    start_price_eur: "",
    desired_montage_window: "",
    notes_for_installer: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.deadline) {
      toast.error("Deadline ist Pflicht");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        deadline: new Date(form.deadline).toISOString(),
        reserve_price_eur: form.reserve_price_eur ? Number(form.reserve_price_eur) : null,
        start_price_eur: form.start_price_eur ? Number(form.start_price_eur) : null,
        desired_montage_window: form.desired_montage_window || null,
        notes_for_installer: form.notes_for_installer || null,
      };
      await api.post(`/projects/${projectId}/auction`, payload);
      toast.success("Auktion gestartet — Installateure sehen sie sofort.");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auktion konnte nicht gestartet werden");
    } finally {
      setBusy(false);
    }
  };

  const inp = "h-9 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[12.5px]";
  const lbl = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-3" onClick={onClose} data-testid="auction-start-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-md w-full max-h-[92vh] overflow-y-auto p-5 space-y-3">
        <div className="flex items-center justify-between border-b border-zinc-800 pb-3 mb-2">
          <div className="flex items-center gap-2"><Gavel className="text-[#00E575]" size={18} /><h2 className="font-display text-lg font-bold">Auktion starten</h2></div>
          <button onClick={onClose} className="h-8 w-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center"><X size={14} /></button>
        </div>
        <div>
          <label className={lbl}>Deadline *</label>
          <input type="datetime-local" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="auction-start-deadline" className={inp} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Reservepreis (€)</label>
            <input type="number" step="0.01" value={form.reserve_price_eur} onChange={(e) => setForm({ ...form, reserve_price_eur: e.target.value })} placeholder="max. Preis (hidden)" data-testid="auction-start-reserve" className={inp} />
          </div>
          <div>
            <label className={lbl}>Startpreis (€, optional)</label>
            <input type="number" step="0.01" value={form.start_price_eur} onChange={(e) => setForm({ ...form, start_price_eur: e.target.value })} placeholder="sichtbarer Anker" className={inp} />
          </div>
        </div>
        <div>
          <label className={lbl}>Wunsch-Montagezeitraum</label>
          <input type="text" value={form.desired_montage_window} onChange={(e) => setForm({ ...form, desired_montage_window: e.target.value })} placeholder="z. B. KW 28/29" className={inp} />
        </div>
        <div>
          <label className={lbl}>Hinweise für Installateur</label>
          <textarea rows={3} value={form.notes_for_installer} onChange={(e) => setForm({ ...form, notes_for_installer: e.target.value })} placeholder="z. B. Hebebühne erforderlich, 3. OG ohne Aufzug, …" className={`${inp} h-auto py-2`} />
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 text-[12px] text-amber-200 flex items-start gap-1.5">
          <AlertCircle size={12} className="shrink-0 mt-0.5" />
          <span>Reservepreis ist <b>versteckt</b> — Installateure erhalten bei Überschreitung nur eine anonyme Fehlermeldung.</span>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
          <button onClick={onClose} className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-medium">Abbrechen</button>
          <button onClick={submit} disabled={busy} data-testid="auction-start-submit" className="h-9 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Auktion starten
          </button>
        </div>
      </div>
    </div>
  );
}
